import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Trash2 } from "lucide-react";

const Th = ({ children }) => (
  <th className="text-left px-2 py-1.5 label-mono text-white whitespace-nowrap">{children}</th>
);
const Field = ({ label, children }) => (
  <label className="block">
    <div className="label-mono mb-1">{label}</div>
    {children}
  </label>
);

/**
 * Manages the SMTP sender accounts used to email invoices. Lives in its own file
 * so Invoices.jsx stays under the Babel-AST size where modals start clashing.
 */
export default function SenderManagerModal({ senders, onClose, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [provider, setProvider] = useState("gmail");
  const [emailAddr, setEmailAddr] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [appPassword, setAppPassword] = useState("");
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUseTls, setSmtpUseTls] = useState(true);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setAdding(false); setProvider("gmail"); setEmailAddr(""); setDisplayName("");
    setAppPassword(""); setSmtpHost(""); setSmtpPort(587); setSmtpUseTls(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!emailAddr || !appPassword) { toast.error("Email & app password required"); return; }
    setSaving(true);
    try {
      const payload = { email: emailAddr, app_password: appPassword, display_name: displayName, provider };
      if (provider !== "gmail") {
        payload.smtp_host = smtpHost;
        payload.smtp_port = Number(smtpPort);
        payload.smtp_use_tls = smtpUseTls;
      }
      await api.post("/invoices/sender-accounts", payload);
      toast.success("Sender added — SMTP login verified");
      reset();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add sender");
    }
    setSaving(false);
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.email}?`)) return;
    try {
      await api.delete(`/invoice-senders/${s.id}`);
      toast.success("Removed");
      onChanged?.();
    } catch { toast.error("Remove failed"); }
  };

  return (
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div className="brut-card max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-6 pt-6 pb-3 border-b border-zinc-200 bg-white shrink-0">
          <div>
            <div className="label-mono">INVOICE EMAIL CONFIG</div>
            <h2 className="font-heading text-2xl font-black">Email Senders</h2>
            <p className="text-xs font-mono text-zinc-600 mt-1">SMTP accounts used only for sending invoices. Use a Gmail App Password (not your login password).</p>
          </div>
          <button onClick={onClose} className="brut-btn-secondary p-1.5 ml-3"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1">
          <div className="border border-zinc-900 mb-4">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-zinc-900 text-white">
                  <Th>Email</Th><Th>Display Name</Th><Th>SMTP</Th><Th></Th>
                </tr>
              </thead>
              <tbody>
                {senders.length === 0 && (
                  <tr><td colSpan={4} className="text-center py-6 text-zinc-400 font-mono text-xs">NO SENDER ACCOUNTS — ADD ONE BELOW</td></tr>
                )}
                {senders.map((s) => (
                  <tr key={s.id} className="border-b border-zinc-200" data-testid={`sender-row-${s.id}`}>
                    <td className="px-2 py-1.5 font-mono text-xs">{s.email}</td>
                    <td className="px-2 py-1.5 text-xs">{s.display_name}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-zinc-600">{s.smtp_host}:{s.smtp_port}{s.smtp_use_tls ? " · TLS" : ""}</td>
                    <td className="px-2 py-1.5">
                      <button onClick={() => remove(s)} className="text-red-600 hover:bg-red-50 p-1" data-testid={`delete-sender-${s.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!adding ? (
            <button onClick={() => setAdding(true)} className="brut-btn px-4 py-2 text-sm" data-testid="add-sender-btn">+ ADD SENDER ACCOUNT</button>
          ) : (
            <form onSubmit={save} className="space-y-3 border-t border-zinc-300 pt-4">
              <Field label="PROVIDER">
                <select value={provider} onChange={(e) => setProvider(e.target.value)} className="brut-input w-full px-3 py-2 text-sm font-mono" data-testid="sender-provider">
                  <option value="gmail">Gmail (App Password) — smtp.gmail.com</option>
                  <option value="custom">Custom SMTP</option>
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="SENDER EMAIL">
                  <input required type="email" value={emailAddr} onChange={(e) => setEmailAddr(e.target.value)} placeholder="invoicing@yourorg.com" className="brut-input w-full px-3 py-2 text-sm" data-testid="sender-email-input" />
                </Field>
                <Field label="DISPLAY NAME">
                  <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Land Drafters Invoicing" className="brut-input w-full px-3 py-2 text-sm" data-testid="sender-name-input" />
                </Field>
              </div>
              <Field label="APP PASSWORD">
                <input required type="password" value={appPassword} onChange={(e) => setAppPassword(e.target.value)} placeholder="16-char app password" className="brut-input w-full px-3 py-2 text-sm font-mono" data-testid="sender-password-input" />
                {provider === "gmail" && (
                  <p className="text-[10px] font-mono text-zinc-500 mt-1">Create at: Google Account → Security → 2-Step Verification → App Passwords</p>
                )}
              </Field>
              {provider !== "gmail" && (
                <div className="grid grid-cols-3 gap-3">
                  <Field label="SMTP HOST">
                    <input required value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} placeholder="smtp.example.com" className="brut-input w-full px-3 py-2 text-sm font-mono" />
                  </Field>
                  <Field label="PORT">
                    <input required type="number" value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)} className="brut-input w-full px-3 py-2 text-sm font-mono" />
                  </Field>
                  <Field label="USE TLS">
                    <select value={smtpUseTls ? "1" : "0"} onChange={(e) => setSmtpUseTls(e.target.value === "1")} className="brut-input w-full px-3 py-2 text-sm font-mono">
                      <option value="1">Yes (STARTTLS)</option>
                      <option value="0">No (use 465 for SSL)</option>
                    </select>
                  </Field>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={reset} className="brut-btn-secondary px-4 py-2 text-sm">CANCEL</button>
                <button type="submit" disabled={saving} className="brut-btn px-4 py-2 text-sm disabled:opacity-60" data-testid="save-sender-btn">
                  {saving ? "VERIFYING…" : "VERIFY & SAVE"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
